import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { departments } from "@/mocks/departments";
import { useAuth } from "@/hooks/useAuth";
import { fetchLiveGroups } from "@/services/zammadService";

export default function BIDFooter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [permittedDepts, setPermittedDepts] = useState<{ id: string; name: string; icon: string }[]>([]);

  // Slugify helper to match group names to paths
  const getSlugForGroupName = (name: string) => {
    const lower = (name || "").toLowerCase().trim();
    if (lower.includes("servicio de ti") || lower === "ti") return "ti";
    if (lower.includes("administracion")) return "administracion";
    if (lower.includes("servicios generales")) return "servicios-generales";
    if (lower.includes("talento humano")) return "talento-humano";
    if (lower.includes("legal")) return "legal";
    if (lower.includes("contraloria")) return "contraloria";
    if (lower.includes("diseno") || lower.includes("marketing")) return "diseno-marketing";

    return lower
      .replace(/[áéíóúü]/g, (match) => {
        const map: Record<string, string> = { á: "a", é: "e", í: "i", ó: "o", ú: "u", ü: "u" };
        return map[match] || match;
      })
      .replace(/ñ/g, "n")
      .replace(/[^a-z0-9]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  useEffect(() => {
    const cacheKey = user ? `zammad_permitted_microsites_${user.id}` : "zammad_permitted_microsites_public";

    // Helper to resolve groups from API response
    function resolveGroups(res: { categoryTree?: any[]; groups?: any[] }) {
      const rootCategories = res.categoryTree || [];
      if (rootCategories.length === 0 && res.groups && res.groups.length > 0) {
        const rootGroupNames = Array.from(new Set(res.groups.map((g: any) => g.name.split("::")[0])));
        return rootGroupNames.map((name) => {
          const slug = getSlugForGroupName(name);
          const staticInfo = departments.find((d) => d.id === slug);
          return {
            id: slug,
            name: staticInfo?.name || name,
            icon: staticInfo?.icon || "ri-organization-chart"
          };
        });
      }

      return rootCategories.map((c: any) => {
        const slug = getSlugForGroupName(c.name);
        const staticInfo = departments.find((d) => d.id === slug);
        return {
          id: slug,
          name: staticInfo?.name || c.name,
          icon: staticInfo?.icon || "ri-organization-chart"
        };
      });
    }

    // 1. Show cached data immediately (prevent layout shift/flicker)
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const resolved = JSON.parse(cached);
        const mapped = resolved.map((item: any) => {
          const staticInfo = departments.find((d) => d.id === item.id);
          return {
            id: item.id,
            name: staticInfo?.name || item.name,
            icon: item.icon || "ri-organization-chart"
          };
        });
        setPermittedDepts(mapped);
      } catch (_) {
        // Ignore stale or malformed cache entries and revalidate below.
      }
    }

    // 2. Refresh from Zammad in background
    let active = true;
    async function revalidate() {
      try {
        const res = await fetchLiveGroups(user ? { userId: user.id, email: user.email } : undefined);
        if (!active) return;
        if (res.error) return;
        const resolved = resolveGroups(res);
        if (resolved) {
          const fresh = JSON.stringify(resolved);
          if (fresh !== cached) {
            setPermittedDepts(resolved);
            sessionStorage.setItem(cacheKey, fresh);
          }
        }
      } catch (err) {
        console.error("Failed to load footer groups:", err);
      }
    }
    revalidate();
    return () => {
      active = false;
    };
  }, [user]);

  const halfLength = Math.ceil(permittedDepts.length / 2);
  const firstCol = permittedDepts.slice(0, halfLength);
  const secondCol = permittedDepts.slice(halfLength);

  return (
    <footer className="w-full bg-foreground-900 text-white">
      <div className="px-4 md:px-6 lg:px-10 py-12 md:py-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-10">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-2">
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigate("/"); }}
              className="flex items-center gap-2.5 mb-4 cursor-pointer"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/a7310928-0a5d-4a69-bde0-29368ee52db9.png"
                alt="BID Group"
                className="h-10 w-auto object-contain brightness-0 invert drop-shadow-[0_0_6px_rgba(255,255,255,0.4)]"
              />
            </a>
            <p className="text-foreground-400 text-sm leading-relaxed mb-5 max-w-sm">
              Portal corporativo de servicios internos. Gestiona tus solicitudes de soporte, administración, tecnología, contraloría, diseño, servicios generales y talento humano en un solo lugar.
            </p>
            <div className="flex items-center gap-3">
              <a href="#" className="w-9 h-9 rounded-full bg-foreground-800 flex items-center justify-center cursor-pointer hover:bg-primary-500 transition-colors" aria-label="LinkedIn">
                <i className="ri-linkedin-line text-white/80 text-sm"></i>
              </a>
              <a href="#" className="w-9 h-9 rounded-full bg-foreground-800 flex items-center justify-center cursor-pointer hover:bg-primary-500 transition-colors" aria-label="Twitter">
                <i className="ri-twitter-x-line text-white/80 text-sm"></i>
              </a>
              <a href="#" className="w-9 h-9 rounded-full bg-foreground-800 flex items-center justify-center cursor-pointer hover:bg-primary-500 transition-colors" aria-label="Instagram">
                <i className="ri-instagram-line text-white/80 text-sm"></i>
              </a>
            </div>
          </div>

          {/* Micrositios */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">
              Micrositios
            </h4>
            <ul className="space-y-2.5">
              {firstCol.map((dept) => (
                <li key={dept.id}>
                  <a
                    href={`/${dept.id}`}
                    onClick={(e) => { e.preventDefault(); navigate(`/${dept.id}`); }}
                    className="text-sm text-foreground-300 hover:text-white transition-colors flex items-center gap-2 cursor-pointer"
                  >
                    <i className={`${dept.icon} text-xs text-foreground-500`}></i>
                    {dept.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Más Micrositios */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">
              &nbsp;
            </h4>
            <ul className="space-y-2.5">
              {secondCol.map((dept) => (
                <li key={dept.id}>
                  <a
                    href={`/${dept.id}`}
                    onClick={(e) => { e.preventDefault(); navigate(`/${dept.id}`); }}
                    className="text-sm text-foreground-300 hover:text-white transition-colors flex items-center gap-2 cursor-pointer"
                  >
                    <i className={`${dept.icon} text-xs text-foreground-500`}></i>
                    {dept.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contacto */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">
              Contacto
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5">
                <i className="ri-mail-line text-sm text-foreground-500 mt-0.5"></i>
                <a href="mailto:contacto@bidgroup.com" className="text-sm text-foreground-300 hover:text-white transition-colors cursor-pointer">
                  contacto@bidgroup.com
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="ri-phone-line text-sm text-foreground-500 mt-0.5"></i>
                <a href="tel:+525551234567" className="text-sm text-foreground-300 hover:text-white transition-colors cursor-pointer">
                  +52 55 5123 4567
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="ri-map-pin-line text-sm text-foreground-500 mt-0.5"></i>
                <span className="text-sm text-foreground-300">
                  Av. Reforma 222, Juárez, CDMX
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-foreground-800">
        <div className="px-4 md:px-6 lg:px-10 py-5 max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} BID Group. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors cursor-pointer">Privacidad</a>
            <a href="#" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors cursor-pointer">Términos</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
